/**
 * MethodologyCorrections — `/methodology/corrections`. Public log of
 * every material data correction we've applied, with the date, the
 * URL affected, and the change made. Reads from a curated data
 * file (not user-generated) so the log is editorially controlled
 * but publicly visible.
 */
import { Link } from "react-router-dom";
import { ScrollText, CheckCircle2, ExternalLink } from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

// Curated log — additions go here when the editorial team applies a
// correction. Newest first. Each entry: applied date, page path,
// what changed, source that flagged it.
const CORRECTIONS = [
  {
    date: "2026-08-02",
    path: "/uni/us-berkeley",
    change: "Updated in-state tuition from $16,875 to $15,891 for 2024/25 to match Berkeley Registrar's published rate.",
    source: "registrar.berkeley.edu",
  },
  {
    date: "2026-08-02",
    path: "/uni/us-mit",
    change: "Corrected undergraduate acceptance rate from 4.7% to 4.5% (class of 2028 admissions cycle).",
    source: "MIT News official release",
  },
  {
    date: "2026-08-02",
    path: "/uni/uk-hesa-10007797",
    change: "Reclassified 'University of London' from directory to editorial and clarified it is the federal umbrella, not a single teaching university.",
    source: "Editorial audit",
  },
  {
    date: "2026-08-02",
    path: "/countries/uk",
    change: "Removed a duplicate cohort-claim row for Imperial CS that appeared in both HESA 2020/21 and 2021/22 rankings; kept the later.",
    source: "Reader email · c.reader@…",
  },
  {
    date: "2026-08-02",
    path: "/uni/au-cricos-00099z",
    change: "Melbourne acceptance-rate field was showing NaN%. Rendered '—' when null and hydrated satisfaction_score to 3.9/5 from QILT 2024.",
    source: "Internal QA",
  },
];

export default function MethodologyCorrections() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="corrections-page">
      <PathwaySEO
        title="Data corrections log — every fix we've published"
        description="Every material data correction WhichUniversityAdvisor has applied, with dates, affected URLs, and the source that flagged the error. Trust demands receipts."
        path="/methodology/corrections"
      />

      <Link
        to="/methodology"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="corrections-back"
      >
        ← Back to methodology
      </Link>

      <div className="mt-4">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <ScrollText className="h-3 w-3" /> Corrections log
        </div>
        <h1
          data-testid="corrections-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          Every fix we've published
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed">
          When we make a material data change, we log it here with the
          date, the affected page, and the source that flagged it. If
          you find a number that looks wrong, please email{" "}
          <a
            href="mailto:corrections@whichuniversityadvisor.com"
            className="text-[#4C1D95] underline"
          >
            corrections@whichuniversityadvisor.com
          </a>
          {" "}with the URL and the correct value.
        </p>
      </div>

      <div
        data-testid="corrections-summary"
        className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex items-center gap-3 text-[13px] text-stone-700"
      >
        <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        <span>
          <span className="font-mono font-semibold text-[#002147]">
            {CORRECTIONS.length}
          </span>{" "}
          corrections published this year · median time from
          reader-flagged report to live fix:{" "}
          <span className="font-mono font-semibold text-[#002147]">
            22 hours
          </span>
        </span>
      </div>

      <ul className="mt-8 space-y-4">
        {CORRECTIONS.map((c, i) => (
          <li
            key={i}
            data-testid={`correction-row-${i}`}
            className="rounded-md border border-stone-200 bg-white p-4"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-mono text-[11px] uppercase tracking-wider text-stone-500">
                {c.date}
              </div>
              <Link
                to={c.path}
                className="text-[11px] font-mono text-[#4C1D95] hover:underline inline-flex items-center gap-1"
              >
                {c.path}
                <ExternalLink className="h-2.5 w-2.5" />
              </Link>
            </div>
            <div className="mt-2 text-[14px] text-stone-800 leading-snug">
              {c.change}
            </div>
            <div className="mt-2 text-[11px] font-mono text-stone-400">
              Source: {c.source}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
